import {find} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetCustomerV2Query, useGetVehicleCustomerQuery} from '@dms/api/customer';
import {useGetServiceOrderByRentalOrderIdQuery} from '@dms/api/rental';
import {useGetVehicleV2Query} from '@dms/api/sales';
import {
  useGetMetadaServiceCaseQuery,
  useGetServiceCaseOrderJobsQuery,
  useGetServiceOrderQuery,
  useGetServiceOrderTypesQuery,
  useGetServiceOrderVariantQuery,
} from '@dms/api/workshop';
import {is404ApiError} from '@dms/shared';

export const usePairedServiceOrder = (rentalOrderId: string) => {
  const {
    data: transactionData,
    isLoading: isLoadingTransaction,
    isFetching: isFetchingTransaction,
    isError: isErrorTransaction,
    error: errorTransaction,
  } = useGetServiceOrderByRentalOrderIdQuery({rentalOrderId});

  const serviceCaseId = transactionData?.serviceCaseId ?? '';
  const serviceOrderId = transactionData?.serviceOrderId ?? '';

  const {
    data: serviceOrder,
    isLoading: isLoadingServiceOrder,
    isFetching: isFetchingServiceOrder,
    isError: isErrorServiceOrder,
  } = useGetServiceOrderQuery(
    {serviceCaseId, serviceOrderId},
    {
      refetchOnMountOrArgChange: true,
      skip: isNilOrEmpty(serviceCaseId) || isNilOrEmpty(serviceOrderId),
    }
  );

  const {
    data: serviceOrderVariant,
    isLoading: isLoadingServiceOrderVariant,
    isFetching: isFetchingServiceOrderVariant,
    isError: isErrorServiceOrderVariant,
  } = useGetServiceOrderVariantQuery(
    {serviceOrderVariantId: serviceOrder?.serviceOrderVariantId ?? ''},
    {skip: isNilOrEmpty(serviceOrder?.serviceOrderVariantId)}
  );

  const {
    data: serviceCase,
    isLoading: isLoadingServiceCase,
    isFetching: isFetchingServiceCase,
    isError: isErrorServiceCase,
  } = useGetMetadaServiceCaseQuery({serviceCaseId}, {skip: isNilOrEmpty(serviceCaseId)});

  const {
    data: serviceOrderTypes,
    isLoading: isLoadingServiceOrderTypes,
    isFetching: isFetchingServiceOrderTypes,
    isError: isErrorServiceOrderTypes,
  } = useGetServiceOrderTypesQuery();

  const {
    data: vehicleCustomers,
    isLoading: isLoadingVehicleCustomers,
    isFetching: isFetchingVehicleCustomers,
    isError: isErrorVehicleCustomers,
  } = useGetVehicleCustomerQuery(
    {vehicleId: serviceCase?.vehicleId ?? ''},
    {skip: isNilOrEmpty(serviceCase?.vehicleId)}
  );

  const {
    data: servicedVehicle,
    isLoading: isLoadingVehicle,
    isFetching: isFetchingVehicle,
    isError: isErrorVehicle,
  } = useGetVehicleV2Query(
    {vehicleId: serviceCase?.vehicleId ?? ''},
    {skip: isNilOrEmpty(serviceCase?.vehicleId)}
  );

  const {
    data: serviceOrderJobs,
    isLoading: isLoadingServiceOrderJob,
    isFetching: isFetchingServiceOrderJob,
    isError: isErrorServiceOrderJob,
  } = useGetServiceCaseOrderJobsQuery(
    {serviceCaseId, serviceOrderId},
    {
      skip: isNilOrEmpty(serviceCaseId) || isNilOrEmpty(serviceOrderId),
    }
  );

  const vehicleOperator = find(
    (vehicleCustomer) => vehicleCustomer?.customerType === 'CORVEHICLECUSTOMERTYPE_OPERATOR',
    vehicleCustomers ?? []
  );

  const {
    data: vehicleOperatorCustomer,
    isLoading: isLoadingVehicleOperatorCustomer,
    isFetching: isFetchingVehicleOperatorCustomer,
    isError: isErrorVehicleOperatorCustomer,
  } = useGetCustomerV2Query(
    {customerId: vehicleOperator?.customerId ?? ''},
    {skip: !vehicleOperator?.customerId}
  );

  const isEmpty = is404ApiError(errorTransaction) || isNilOrEmpty(transactionData);

  const isFetching =
    isFetchingTransaction ||
    isFetchingServiceOrder ||
    isFetchingServiceOrderVariant ||
    isFetchingServiceCase ||
    isFetchingServiceOrderTypes ||
    isFetchingVehicleCustomers ||
    isFetchingVehicleOperatorCustomer ||
    isFetchingVehicle ||
    isFetchingServiceOrderJob;

  const isLoading =
    isLoadingTransaction ||
    isLoadingServiceOrder ||
    isLoadingServiceOrderVariant ||
    isLoadingServiceCase ||
    isLoadingServiceOrderTypes ||
    isLoadingVehicleCustomers ||
    isLoadingVehicleOperatorCustomer ||
    isLoadingVehicle ||
    isLoadingServiceOrderJob;
  const isError =
    isErrorTransaction ||
    isErrorServiceOrder ||
    isErrorServiceOrderVariant ||
    isErrorServiceCase ||
    isErrorServiceOrderTypes ||
    isErrorVehicleCustomers ||
    isErrorVehicleOperatorCustomer ||
    isErrorVehicle ||
    isErrorServiceOrderJob;

  return {
    serviceOrder,
    serviceOrderVariant,
    serviceOrderTypes,
    vehicleOperatorCustomer,
    servicedVehicle,
    serviceOrderJobs,
    isLoading: isLoading || isFetching,
    isError,
    isEmpty,
  };
};
